import { PriceChartApiResponse } from '../../../../Detail/Charts';
import { CandleSticksLatestOrder } from '../../../../Detail/Charts/types';

import './index.css';
import { ComponentDimensions } from '../../../../../types/Layout';
import CandleSticks from '../../../../../components/Charts/CandleSticks';
import { CandleType } from '../../../../Detail/types';
import { GenericTransactionInfo } from '../../../../../types/Transaction';
import { Exchange } from 'grts-utils/types/Exchange';
import { CustomTransaction } from 'grts-utils/types/Transaction';
import { TimeFrame } from '../../../../../types/Bot';
import { useMemo } from 'react';
import { RSIItem } from '../../../../ProfitCalculations/types';
import MultiChart from '../../../../../components/MultiChart';
import RSIChart from '../../../../../components/Charts/Rsi';
import { IndicatorConfig } from 'grts-utils/types/ProfitCalcs';

interface Props {
  rootDimensions: ComponentDimensions | undefined;
  candles: PriceChartApiResponse | undefined;
  timeFrame: TimeFrame | undefined;
  isLoading: boolean;
  multiOrderCandles?: PriceChartApiResponse | undefined;
  bundledTransactions: Record<string, GenericTransactionInfo[]>;
  genericTransactions: Array<GenericTransactionInfo | CustomTransaction>;
  candleSticksLatestOrder?: CandleSticksLatestOrder;
  baseAsset: string;
  quoteAsset: string;
  exchange: Exchange;
  transactionTypeToggleValues?: {
    core: boolean;
    takeProfit: boolean;
    stopLoss: boolean;
  };
  rsiIndicatorValuesOppositeOrder?: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder?: RSIItem[] | undefined;
  indicatorConfig?: IndicatorConfig;
}

/**
 * We want to show real transactions and profit calc transactions on candles
 * I want to be able to zoomIn on shorter and multi
 *
 * When I press a transaction,
 * I want to see a zoomedIn state from both real and profit calcs transaction
 * (What if one is just completely missing?)
 *
 * What about the different profit calcs from real transactions
 * 1.Take the last one and start at the timestamp of the previous transaction
 * 2. Make sure we do a profit calc for each different complex params found in the transactions
 *
 * We need:
 * The candles to show
 * transaction dots of the real transactions
 * transaction dots of profit calc transactions
 * Feature to handle the press on the transaction
 * Hover shows the transaction info
 */

const TransactionCandles = (props: Props) => {
  const {
    rootDimensions,
    timeFrame,
    candles,
    multiOrderCandles,
    bundledTransactions,
    genericTransactions,
    isLoading,
    candleSticksLatestOrder,
    baseAsset,
    quoteAsset,
    exchange,
    transactionTypeToggleValues,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
    indicatorConfig,
  } = props;

  /** ============================================== EFFECTS ============================================== */

  const filteredGenericTransactions = useMemo(() => {
    return genericTransactions.filter((transaction) => {
      if (!transactionTypeToggleValues) return true;

      if (
        transaction.transactionType.includes('TAKE_PROFIT') &&
        transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.stopLoss
      ) {
        return true;
      }

      if (
        transaction.transactionType.includes('TAKE_PROFIT') &&
        !transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.takeProfit
      ) {
        return true;
      }

      if (
        !transaction.transactionType.includes('TAKE_PROFIT') &&
        !transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.core
      ) {
        return true;
      }
    });
  }, [genericTransactions, transactionTypeToggleValues]);

  return (
    <div className="detail-transactions-candles-root" style={{ marginTop: 10 }}>
      <MultiChart dimensions={rootDimensions}>
        <CandleSticks
          dimensions={rootDimensions}
          candleResponse={candles}
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
          exchange={exchange}
          timeFrame={timeFrame}
          candleType={CandleType.Normal}
          isLoading={isLoading}
          transactions={filteredGenericTransactions}
          transactionsByTimestamp={bundledTransactions}
          multiOrderCandles={multiOrderCandles}
          // getIndividualTransactions={getIndividualTransactions}
          candleSticksLatestOrder={candleSticksLatestOrder}
          scale={'linear'}
          useIndicators={true}
        />
        {rsiIndicatorValuesOppositeOrder && !!indicatorConfig && (
          <RSIChart
            key={'Opposite Order Relative Strength Index'}
            label={`Opposite Order Relative Strength Index (p:${indicatorConfig.oppositeOrderPeriod})`}
            rsi={rsiIndicatorValuesOppositeOrder}
            overExtensionThreshold={indicatorConfig.oppositeOrderThreshold}
            showXAxis={false}
          />
        )}
        {rsiIndicatorValuesOppositeOrder && !!indicatorConfig && (
          <RSIChart
            key={'Same Order Relative Strength Index'}
            label={`Same Order Relative Strength Index (p:${indicatorConfig.sameOrderPeriod})`}
            rsi={rsiIndicatorValuesSameOrder}
            overExtensionThreshold={indicatorConfig.sameOrderThreshold}
            showXAxis={false}
          />
        )}
      </MultiChart>
    </div>
  );
};

export default TransactionCandles;
